import { type AthleteItem, type AthleteSortKey, PARTICIPANTS_PAGE_SIZE_DEFAULT } from "@seagames/contracts";
import { useFilterDraft } from "@seagames/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AthletesFilters } from "../../components/athletes/athletes-filters";
import { AthletesTable } from "../../components/athletes/athletes-table";
import { athletesListQuery } from "../../components/athletes/queries";
import {
	type AthletesDraft,
	type AthletesSearch,
	EMPTY_ATHLETES_DRAFT,
	athletesDraftFrom,
	athletesFilterCount,
	athletesPaging,
	athletesParams,
	athletesSearch,
	athletesSearchFrom,
	athletesSort,
} from "../../components/athletes/search";
import { PAGE_SIZE_CHOICES, pageBeyondEnd, pageToUrl, sizeToUrl } from "../../components/nocs/list-params";
import { nocOptionsQuery } from "../../components/nocs/queries";
import { useOnGamesSwitch, useScrollToTopOnPage } from "../../components/nocs/use-list-page";
import { useOpenOnSessionNoc } from "../../components/nocs/use-session-noc";
import { PageHeader } from "../../components/page/page-header";
import { Button } from "../../components/ui/button";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { errorMessage } from "../../lib/api/messages";
import { gamesSportsQuery, useCurrentGames } from "../../lib/games";
import { formatInteger } from "../../lib/i18n/format";

/**
 * Participants › Athletes — the people entered in the current games, one
 * page at a time (`GET /athletes`, offset pages with the total). Filters in
 * the URL (`?q&noc&sport&event&gender&sort&page&size`). An NOC login opens
 * on its own NOC: on a large games the whole-games list is the costliest read
 * (R3's measurement), and that is the list such a user wants anyway.
 */
export const Route = createFileRoute("/_app/athletes/")({
	validateSearch: athletesSearch,
	component: AthletesPage,
});

function AthletesPage() {
	const { t } = useTranslation("athletes");
	const search = Route.useSearch();
	const { session } = Route.useRouteContext();
	const navigate = useNavigate({ from: "/athletes/" });
	const current = useCurrentGames();
	const gamesId = current.gamesId;

	const sports = useQuery({ ...gamesSportsQuery(gamesId ?? ""), enabled: gamesId !== null });
	const nocs = useQuery({ ...nocOptionsQuery(gamesId ?? ""), enabled: gamesId !== null });

	const go = useCallback((next: AthletesSearch, replace = false) => void navigate({ search: next, replace }), [navigate]);
	const applied = useMemo(() => athletesDraftFrom(search), [search]);
	const onApply = useCallback((d: AthletesDraft) => go(athletesSearchFrom(d, { sort: search.sort, size: search.size })), [go, search.sort, search.size]);
	const f = useFilterDraft<AthletesDraft>(applied, onApply, EMPTY_ATHLETES_DRAFT);
	const filterCount = athletesFilterCount(search);

	const ready = useOpenOnSessionNoc({
		session,
		urlHasFilters: filterCount > 0,
		gamesNocIds: nocs.isError ? [] : nocs.data?.items.map((n) => n.responsible_organization_id),
		apply: (noc) => navigate({ search: { ...search, noc, page: undefined }, replace: true }),
	});

	// The sidebar switched games: sports and events belong to the old one.
	useOnGamesSwitch(gamesId, () => go({ ...search, sport: undefined, event: undefined, page: undefined }, true));

	const params = gamesId !== null && ready ? athletesParams(gamesId, search) : null;
	const list = useQuery({
		...athletesListQuery(params ?? { games_id: "", page: 1, page_size: PARTICIPANTS_PAGE_SIZE_DEFAULT }),
		enabled: params !== null,
		placeholderData: keepPreviousData,
	});

	const { page, pageSize } = athletesPaging(search);
	// A page past the end (a stale link, fewer rows now): show the last page instead.
	const beyond = list.data && list.data.items.length === 0 ? pageBeyondEnd(page, list.data.total, pageSize) : null;
	useEffect(() => {
		if (beyond !== null) go({ ...search, page: pageToUrl(beyond) }, true);
	}, [beyond]);

	const top = useRef<HTMLDivElement>(null);
	useScrollToTopOnPage(top, page);

	const open = (a: AthleteItem) =>
		void navigate({ to: "/athletes/$personId", params: { personId: a.people_id }, search: { games: gamesId ?? undefined } });

	if (current.error) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("errors.gamesTitle")} body={errorMessage(current.error)} />
			</div>
		);
	}
	if (current.isLoading) return <Spinner className="size-[1.5rem] text-board" />;
	if (!current.games || gamesId === null) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("empty.noGamesTitle")} body={t("empty.noGamesBody")} />
			</div>
		);
	}

	const total = list.data?.total ?? 0;
	const rows = list.data?.items ?? [];

	return (
		<div className="flex flex-col">
			<PageHeader title={t("title")} subtitle={t("subtitle", { games: current.games.name })} />

			<div className="flex flex-col gap-[1rem]">
				<AthletesFilters
					gamesId={gamesId}
					draft={f.draft}
					set={f.set}
					onApply={f.apply}
					onReset={f.reset}
					activeCount={filterCount}
					sports={sports.data ?? []}
					nocs={nocs.data?.items ?? []}
				/>

				<div ref={top} className="scroll-mt-[4.5rem]">
					<AthletesTable
						rows={rows}
						loading={!ready || list.isPending || (list.isFetching && list.isPlaceholderData)}
						error={list.isError ? { message: errorMessage(list.error), onRetry: () => void list.refetch() } : null}
						empty={
							filterCount > 0
								? {
										title: t("empty.filteredTitle"),
										description: t("empty.filteredBody"),
										action: (
											<Button variant="outline" size="sm" onClick={f.reset}>
												{t("empty.reset")}
											</Button>
										),
									}
								: { title: t("empty.noneTitle", { games: current.games.name }), description: t("empty.noneBody") }
						}
						toolbar={
							list.data ? (
								<span className="text-[0.8125rem] text-muted" aria-live="polite">
									{t("table.count", { count: total, n: formatInteger(total) })}
								</span>
							) : null
						}
						pagination={
							list.data && total > 0
								? {
										page,
										pageSize,
										total,
										onPageChange: (p) => go({ ...search, page: pageToUrl(p) }),
										onPageSizeChange: (s) => go({ ...search, size: sizeToUrl(s, PARTICIPANTS_PAGE_SIZE_DEFAULT), page: undefined }),
										pageSizeOptions: PAGE_SIZE_CHOICES,
									}
								: undefined
						}
						sort={athletesSort(search)}
						onSort={(s: AthleteSortKey) => {
							if (s !== athletesSort(search)) go({ ...search, sort: s === "name" ? undefined : s, page: undefined });
						}}
						onOpen={open}
					/>
				</div>
			</div>
		</div>
	);
}
